import React, { useRef, useEffect, useState, useCallback } from 'react';
import MarcEtapa from './MarcEtapa';
import { useTranslation } from 'react-i18next';
import { LLENC, LLAPIS, GRUIXOS_LLAPIS } from '../../tema';
import {
  dibuixaSimbol, dibuixaFletxa, aQuinElement, aQuinaFletxa,
} from '../../imatgeRica';
import {
  CursorArrowRaysIcon,
  PencilIcon,
  UserIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  CogIcon,
  ChatBubbleLeftRightIcon,
  FaceSmileIcon,
  FaceFrownIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const RichPictureStage = ({ data, onUpdate, onNext }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const capsaRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState('select');
  const [fletxes, setFletxes] = useState(data.richPicture?.arrows || []);
  const [traçantFletxa, setTraçantFletxa] = useState(null);
  const [fletxaTriada, setFletxaTriada] = useState(null);
  const arrossegant = useRef(null);
  const [elements, setElements] = useState(data.richPicture?.elements || []);
  // ELS TRAÇOS DEL LLAPIS ES DESEN, i abans no.
  //
  // `handleMouseMove` pintava directament al context del `canvas` i **no en guardava res**. I
  // encara pitjor: l'efecte de redibuixar fa `clearRect` cada cop que canvien els elements, o
  // sigui que **posar-hi una persona esborrava tot el que havies dibuixat a mà**. En una imatge
  // rica, on el dibuix a mà és la meitat del que es fa, això és perdre la feina a cada clic.
  //
  // Es guarden com a **llistes de punts i no com una imatge**: així es poden tornar a dibuixar, i
  // el dia que aquest llenç passi a SVG —com els altres dos— o que se n'hagi d'exportar un PDF
  // vectorial, els traços hi van. Una imatge rasteritzada tancaria aquella porta.
  const [strokes, setStrokes] = useState(data.richPicture?.strokes || []);
  const traçActual = useRef(null);
  const [colorLlapis, setColorLlapis] = useState(LLAPIS[0].valor);
  const [gruixLlapis, setGruixLlapis] = useState(GRUIXOS_LLAPIS[1]);
  // Igual que a les definicions arrel: només l'identificador a l'estat. Amb una còpia, escriure
  // al nom d'un element només deixava posar-hi una lletra.
  const [selectedElementId, setSelectedElementId] = useState(null);
  const selectedElement = elements.find((el) => el.id === selectedElementId) || null;
  const setSelectedElement = (el) => setSelectedElementId(el?.id ?? null);
  const [showElementForm, setShowElementForm] = useState(false);

  const tools = [
    // TRIAR HI FALTAVA. Sense això sempre estàs afegint: no es podia agafar un element per
    // moure'l ni per esborrar-lo, que en un dibuix que es fa en grup i es reordena mig cop cada
    // minut és la meitat de la feina.
    { id: 'select', clau: 'toolSelect', icon: CursorArrowRaysIcon, color: 'text-gray-700' },
    { id: 'pen', clau: 'toolPen', icon: PencilIcon, color: 'text-gray-600' },
    { id: 'actor', clau: 'toolActor', icon: UserIcon, color: 'text-primary-600' },
    { id: 'concern', clau: 'toolConcern', icon: ExclamationTriangleIcon, color: 'text-danger-600' },
    { id: 'process', clau: 'toolProcess', icon: CogIcon, color: 'text-ok-600' },
    { id: 'structure', clau: 'toolStructure', icon: HomeIcon, color: 'text-primary-600' },
    { id: 'climate_good', clau: 'toolClimateGood', icon: FaceSmileIcon, color: 'text-ok-500' },
    { id: 'climate_bad', clau: 'toolClimateBad', icon: FaceFrownIcon, color: 'text-danger-500' },
    // La fletxa no és cap icona que es deixi anar: es clica d'on surt i on va.
    { id: 'arrow', clau: 'toolArrow', icon: ArrowRightIcon, color: 'text-gray-700' },
    { id: 'bubble', clau: 'toolBubble', icon: ChatBubbleLeftRightIcon, color: 'text-primary-600' },
  ];
  const ES_SIMBOL = ['actor', 'concern', 'process', 'structure', 'climate_good', 'climate_bad', 'bubble'];

  // EL DIBUIX ES REFÀ TAMBÉ QUAN LA CAIXA CANVIA DE MIDA.
  //
  // El `canvas` té una mida CSS i una mida de mapa de bits, i són coses diferents. La segona es
  // fixava un sol cop i només es refeia quan canviaven els elements: en canviar l'amplada de la
  // finestra —o en plegar la barra d'etapes— **el mapa de bits es quedava com era i el dibuix
  // sortia estirat, i pitjor encara, les coordenades del ratolí deixaven de quadrar amb el que
  // es veia**. Amb un llenç on es col·loquen figures clicant, això és tot el problema.
  const redibuixa = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const amplada = canvas.offsetWidth || 1;
    const alcada = canvas.offsetHeight || 1;
    canvas.width = amplada * 2;
    canvas.height = alcada * 2;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(2, 2);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = LLENC.text;

    ctx.clearRect(0, 0, amplada, alcada);
    drawGrid(ctx);

    // Primer els traços del llapis i després els elements: el que s'ha dibuixat a mà és el fons
    // sobre el qual es col·loquen les figures, i al revés les taparia.
    // Cada traç porta el SEU color i el SEU gruix, i es posen aquí explícitament. Heretar-los
    // del que hi hagués al context és el que va fer que tot sortís gris.
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const traç of strokes) {
      if (!traç.punts || traç.punts.length < 2) continue;
      ctx.strokeStyle = traç.color || LLAPIS[0].valor;
      ctx.lineWidth = traç.gruix || GRUIXOS_LLAPIS[1];
      ctx.beginPath();
      ctx.moveTo(traç.punts[0].x, traç.punts[0].y);
      for (const p of traç.punts.slice(1)) ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
    ctx.restore();

    // Les fletxes van SOTA els símbols: si no, els travessen pel damunt.
    for (const f of fletxes) {
      const o = elements.find((e) => e.id === f.from);
      const d = elements.find((e) => e.id === f.to);
      if (o && d) dibuixaFletxa(ctx, o, d, { triat: fletxaTriada === f.id, etiqueta: f.label || '' });
    }
    for (const el of elements) {
      dibuixaSimbol(ctx, el, { triat: selectedElementId === el.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, strokes, fletxes, selectedElementId, fletxaTriada]);

  useEffect(() => { redibuixa(); }, [redibuixa]);

  useEffect(() => {
    const caixa = capsaRef.current;
    if (!caixa || typeof ResizeObserver === 'undefined') return undefined;
    const observador = new ResizeObserver(() => redibuixa());
    observador.observe(caixa);
    return () => observador.disconnect();
  }, [redibuixa]);

  // `save`/`restore` a posta: sense això la reixa **deixava el seu color i el seu gruix posats**
  // i tot el que es dibuixava després els heretava. És per això que el llapis sortia d'un gris
  // pàl·lid de mig píxel — la mateixa fuita que feia grisos els símbols, per un altre forat.
  const drawGrid = (ctx) => {
    const gridSize = 20;
    ctx.save();
    ctx.strokeStyle = LLENC.reixa;
    ctx.lineWidth = 0.5;
    
    for (let x = 0; x < canvasRef.current.offsetWidth; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvasRef.current.offsetHeight);
      ctx.stroke();
    }
    
    for (let y = 0; y < canvasRef.current.offsetHeight; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvasRef.current.offsetWidth, y);
      ctx.stroke();
    }
    ctx.restore();
  };


  const puntDe = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const desaFletxes = (noves) => {
    setFletxes(noves);
    onUpdate({ richPicture: { ...data.richPicture, elements, strokes, arrows: noves } });
  };

  const handleCanvasClick = (e) => {
    if (currentTool === 'pen') return;
    const { x, y } = puntDe(e);
    const damunt = aQuinElement(elements, x, y);

    // TRIAR: agafar el que hi ha a sota, o deixar-ho anar si no hi ha res.
    if (currentTool === 'select') {
      if (damunt) {
        setSelectedElement(damunt); setFletxaTriada(null); setShowElementForm(true);
        return;
      }
      const f = aQuinaFletxa(fletxes, elements, x, y);
      setFletxaTriada(f ? f.id : null);
      if (!f) { setSelectedElement(null); setShowElementForm(false); }
      return;
    }

    // LA FLETXA ÉS DE DOS CLICS: d'on surt i on va. Una fletxa que no va enlloc no diu res.
    if (currentTool === 'arrow') {
      if (!damunt) { setTraçantFletxa(null); return; }
      if (!traçantFletxa) { setTraçantFletxa(damunt.id); return; }
      if (traçantFletxa !== damunt.id
          && !fletxes.some((f) => f.from === traçantFletxa && f.to === damunt.id)) {
        desaFletxes([...fletxes, { id: `fl_${Date.now()}`, from: traçantFletxa, to: damunt.id, label: '' }]);
        setCurrentTool('select');
      }
      setTraçantFletxa(null);
      return;
    }

    // Afegir un símbol. Si es clica damunt d'un que ja hi és, es tria en comptes d'apilar-n'hi
    // un altre a sobre.
    if (!ES_SIMBOL.includes(currentTool)) return;
    if (damunt) { setSelectedElement(damunt); setShowElementForm(true); return; }
    const nou = {
      id: `el_${Date.now()}`, type: currentTool, x, y, name: '', description: '',
    };
    const llista = [...elements, nou];
    setElements(llista);
    setSelectedElement(nou);
    setShowElementForm(true);
    onUpdate({ richPicture: { ...data.richPicture, elements: llista, strokes, arrows: fletxes } });
  // Torna a triar després d'afegir: deixar l'eina posada és el que fa que acabis omplint el llenç
  // sense voler. La intuïció després de col·locar una cosa és mirar-se-la, no col·locar-ne una
  // altra. NO val per al llapis —dibuixar és continu— ni per a esborrar.
    setCurrentTool('select');
  };

  const esborraTriat = () => {
    if (fletxaTriada) { desaFletxes(fletxes.filter((f) => f.id !== fletxaTriada)); setFletxaTriada(null); return; }
    if (selectedElement) deleteElement();
  };

  const handleMouseDown = (e) => {
    const { x, y } = puntDe(e);
    // MOURE. En mode triar, agafar un símbol i arrossegar-lo. Sense això no es podia recol·locar
    // res un cop posat, i una imatge rica es reordena mig cop cada minut.
    if (currentTool === 'select') {
      const damunt = aQuinElement(elements, x, y);
      if (damunt) arrossegant.current = { id: damunt.id, dx: damunt.x - x, dy: damunt.y - y };
      return;
    }
    if (currentTool !== 'pen') return;
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = colorLlapis;
    ctx.lineWidth = gruixLlapis;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
    traçActual.current = [{ x, y }];
  };

  const handleMouseMove = (e) => {
    const { x, y } = puntDe(e);
    if (arrossegant.current) {
      const { id, dx, dy } = arrossegant.current;
      setElements(elements.map((el) => (el.id === id ? { ...el, x: x + dx, y: y + dy } : el)));
      return;
    }
    if (!isDrawing || currentTool !== 'pen') return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    if (traçActual.current) traçActual.current.push({ x, y });
  };

  const handleMouseUp = () => {
    if (arrossegant.current) {
      arrossegant.current = null;
      onUpdate({ richPicture: { ...data.richPicture, elements, strokes, arrows: fletxes } });
      return;
    }
    setIsDrawing(false);
    // Un traç d'un sol punt és un clic, no un dibuix: no es desa.
    if (traçActual.current && traçActual.current.length > 1) {
      const nous = [...strokes,
        { id: `s_${Date.now()}`, punts: traçActual.current, color: colorLlapis, gruix: gruixLlapis }];
      setStrokes(nous);
      onUpdate({ richPicture: { ...data.richPicture, elements, strokes: nous, arrows: fletxes } });
    }
    traçActual.current = null;
  };

  const esborraTraços = () => {
    setStrokes([]);
    onUpdate({ richPicture: { ...data.richPicture, elements, strokes: [] } });
  };

  const updateElement = (updates) => {
    const updatedElements = elements.map(el => 
      el.id === selectedElement.id ? { ...el, ...updates } : el
    );
    setElements(updatedElements);
    onUpdate({
      richPicture: {
        ...data.richPicture,
        elements: updatedElements
      }
    });
  };

  const deleteElement = () => {
    const updatedElements = elements.filter(el => el.id !== selectedElement.id);
    setElements(updatedElements);
    setShowElementForm(false);
    setSelectedElement(null);
    onUpdate({
      richPicture: {
        ...data.richPicture,
        elements: updatedElements
      }
    });
  };

  // Buidar la imatge se n'ha d'endur TAMBÉ els traços del llapis. Des que es desen a part, un
  // «buida-ho» que només tregui els elements deixaria el dibuix a mà a la pantalla i tornaria a
  // sortir en recarregar: mig buidat és pitjor que no buidar.
  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    drawGrid(ctx);
    setElements([]);
    setStrokes([]);
    onUpdate({ richPicture: { ...data.richPicture, elements: [], strokes: [] } });
  };

  return (
    <MarcEtapa propi className="flex-col">
      {/* LA PALETA VA A DALT, no a una columna de l'esquerra.
          Abans hi havia 256 px de paleta a l'esquerra, 320 px de propietats a la dreta i una
          capçalera de text damunt del llenç. Amb els 320 px de la llista d'etapes, **el llenç
          es quedava amb menys de la meitat de l'amplada i una mica d'alçada**. És la mateixa
          decisió que es va prendre al llenç causal el 25/08/2026 i al d'estocs el 26/08/2026,
          i pel mateix motiu: el que es fa servir mentre es dibuixa ha d'anar a l'alçada dels
          ulls i el llenç ha d'ocupar tot el que queda.
          I el panell de propietats **surt damunt del llenç i només si hi ha res triat**: com a
          columna era fill de la columna del llenç, o sigui que en comptes de posar-se al costat
          se li posava a sobre i el tapava. */}
      <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-1.5 flex-wrap flex-shrink-0">
        {tools.map((tool) => {
          const Icon = tool.icon;
          const isActive = currentTool === tool.id;
          return (
            <button key={tool.id} type="button" onClick={() => setCurrentTool(tool.id)}
              title={t(`ssm.${tool.clau}`)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                          border transition-colors ${isActive
                ? 'bg-primary-700 text-white border-primary-700'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
              <Icon className="h-4 w-4" />
              <span className="hidden lg:inline">{t(`ssm.${tool.clau}`)}</span>
            </button>
          );
        })}

        {currentTool === 'pen' && (
          <>
            <span className="w-px h-6 bg-gray-200 mx-1.5" />
            <div className="flex items-center gap-1">
              {LLAPIS.map((c) => (
                <button key={c.valor} type="button" onClick={() => setColorLlapis(c.valor)}
                  title={t(`ssm.llapis_${c.nom}`)} aria-label={t(`ssm.llapis_${c.nom}`)}
                  className={`w-6 h-6 rounded-md border-2 transition-transform ${
                    colorLlapis === c.valor ? 'border-gray-900 scale-110' : 'border-gray-300 hover:border-gray-500'}`}
                  style={{ backgroundColor: c.valor }} />
              ))}
            </div>
            <div className="flex items-center gap-1 ml-1">
              {GRUIXOS_LLAPIS.map((g) => (
                <button key={g} type="button" onClick={() => setGruixLlapis(g)}
                  title={t('ssm.gruix', { n: g })} aria-label={t('ssm.gruix', { n: g })}
                  className={`w-7 h-7 rounded-md border flex items-center justify-center transition-colors ${
                    gruixLlapis === g ? 'bg-primary-700 border-primary-700' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
                  <span className="block rounded-full"
                    style={{ width: g + 2, height: g + 2,
                             background: gruixLlapis === g ? '#FFFFFF' : colorLlapis }} />
                </button>
              ))}
            </div>
          </>
        )}

        <span className="w-px h-6 bg-gray-200 mx-1.5" />

        {strokes.length > 0 && (
          <button type="button" onClick={esborraTraços}
            className="px-2.5 py-1.5 rounded-md text-xs text-gray-600 border border-gray-300
                       hover:bg-gray-50">
            {t('ssm.clearStrokes')}
          </button>
        )}
        {(selectedElement || fletxaTriada) && (
          <button type="button" onClick={esborraTriat}
            className="px-2.5 py-1.5 rounded-md text-xs font-medium text-danger-700
                       border border-danger-300 hover:bg-danger-50">
            {t('ssm.esborraTriat')}
          </button>
        )}
        <button type="button" onClick={clearCanvas}
          className="px-2.5 py-1.5 rounded-md text-xs text-danger-600 border border-danger-300
                     hover:bg-danger-50">
          {t('ssm.clearCanvas')}
        </button>
        <span className="ml-auto text-xs text-gray-500 hidden xl:block max-w-md truncate">
          {t('ssm.s2Why')}
        </span>
      </div>

      <div ref={capsaRef} className="flex-1 min-h-0 relative" style={{ background: '#FFFFFF' }}>
        {traçantFletxa && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-md
                          bg-white border border-gray-300 shadow-sm text-xs text-gray-600">
            {t('ssm.traçantFletxa')}
          </div>
        )}
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 w-full h-full ${
            currentTool === 'select' ? 'cursor-default' : 'cursor-crosshair'}`}
          onClick={handleCanvasClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />

        {showElementForm && selectedElement && (
          <div className="absolute right-3 top-3 z-20 w-72 bg-white border border-gray-200
                          rounded-md shadow-lg p-4 max-h-[calc(100%-1.5rem)] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">{t('ssm.elementProperties')}</h3>
              <button type="button" onClick={() => setShowElementForm(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('properties.name')}
                </label>
                <input type="text" value={selectedElement.name || ''}
                  onChange={(e) => updateElement({ name: e.target.value })}
                  placeholder={t('catwoe.elementName')}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm
                             focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('properties.notes')}
                </label>
                <textarea rows={3} value={selectedElement.description || ''}
                  onChange={(e) => updateElement({ description: e.target.value })}
                  placeholder={t('catwoe.elementDesc')}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm
                             focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('props.type')}
                </label>
                <select value={selectedElement.type}
                  onChange={(e) => updateElement({ type: e.target.value })}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm
                             focus:ring-2 focus:ring-primary-500">
                  {tools.filter((x) => x.id !== 'pen').map((tool) => (
                    <option key={tool.id} value={tool.id}>{t(`ssm.${tool.clau}`)}</option>
                  ))}
                </select>
              </div>
              <button type="button" onClick={deleteElement}
                className="w-full px-3 py-2 text-sm text-danger-600 border border-danger-300
                           rounded-md hover:bg-danger-50">
                {t('common.remove')}
              </button>
            </div>
          </div>
        )}
      </div>
    </MarcEtapa>
  );
};

export default RichPictureStage;